import styled from "@emotion/styled";
import type { FC } from "react";
import {
  IconCheckBold,
  IconCrossBold,
  IconChevronRight
} from "src/constants/icons";
import type { RefillRequirement } from "src/state/RefillRequirementsCubit/RefillRequirementsTypes";
import { formatRequirementLabel } from "src/state/RefillRequirementsCubit/RefillRequirementsHelpers";
import { RefillColors } from "src/constants/refillColors";

const CHECK_GREEN = RefillColors.inRange;
const CROSS_RED = RefillColors.criticalRed;

// Base is a <button> (reset below) so an actionable row is keyboard-focusable/activatable;
// rendered as a plain <div> when not interactive. `& + &` shares one class across both tags,
// so the row dividers hold regardless.
const Row = styled("button", {
  shouldForwardProp: (p) => p !== "$interactive"
})<{ $interactive: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  min-height: 56px;
  width: 100%;
  margin: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: ${({ $interactive }) => ($interactive ? "pointer" : "default")};

  & + & {
    border-top: 1px solid var(--color-cream-darker);
  }
`;

const Chevron = styled.div`
  display: flex;
  align-items: center;
  align-self: center;
  flex-shrink: 0;
`;

const StatusIcon = styled("div", {
  shouldForwardProp: (p) => p !== "$done"
})<{ $done: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: ${({ $done }) => ($done ? CHECK_GREEN : CROSS_RED)};
`;

const Label = styled.span`
  flex: 1;
  min-width: 0;
  font-family: "STK Bureau", sans-serif;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.4;
  letter-spacing: -0.32px;
  color: var(--color-text);
`;

/**
 * Checklist row: status icon + label. Most requirements are completed via home-screen tasks,
 * so they're display-only. When an `onClick` is supplied (the actionable refill check-in) the
 * row becomes a button with a trailing chevron that opens the refill survey.
 */
const RefillRequirementRow: FC<{
  requirement: RefillRequirement;
  onClick?: () => void;
}> = ({ requirement, onClick }) => {
  const interactive = Boolean(onClick);
  return (
    <Row
      as={interactive ? undefined : "div"}
      type={interactive ? "button" : undefined}
      $interactive={interactive}
      onClick={onClick}
      data-fulfilled={requirement.fulfilled}
    >
      <StatusIcon $done={requirement.fulfilled}>
        {requirement.fulfilled ? (
          <IconCheckBold size={24} />
        ) : (
          <IconCrossBold size={24} />
        )}
      </StatusIcon>
      <Label>{formatRequirementLabel(requirement)}</Label>
      {interactive && (
        <Chevron aria-hidden="true">
          <IconChevronRight />
        </Chevron>
      )}
    </Row>
  );
};

export default RefillRequirementRow;
